<template>
  <b-container class="page">
    <b-row class="mt-2" v-if="err">
      <b-col cols="12" style="min-height: 80%"/>
    </b-row>

    <b-row v-else>
      <b-col cols="12" sm="6" offset-sm="3" lg="4" offset-lg="4">
        <div v-if="contentShare">
          <p class="text-center">{{ $t('contentShares.redirecting-to', { url: '' }).trim() }}</p>
          <content-card :entry="contentShare" hide-footer/>
        </div>
        <div v-else>
          <p class="text-center">{{ $t('general.loading') }}</p>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import qs from 'querystring';
import Page from '@/components/Page';
import HeaderMinimal from '@/components/Header-minimal';
import ContentSharePublicQuery from '@/graphql/queries/ContentSharePublic.gql';
import ContentCard from '@/components/content-share/ContentCard';
import cdnTransform from '@/filters/cdn';
import { captureLog } from '@/lib/logs';
import { mapRoot } from '@/mixins/root';

export default {
  props: {
    shareCode: String,
    url: String
  },
  components: {
    Page,
    HeaderMinimal,
    ContentCard
  },
  data: () => ({
    err: null,
    user: null
  }),
  apollo: {
    contentShare: {
      query: ContentSharePublicQuery,
      variables() {
        const { shareCode } = this;
        return { shareCode };
      },
      update({ company }) {
        const { contentShare } = company || {};
        return contentShare;
      },
      result({ loading, data }) {
        if (!loading && data && data.company) {
          if (data.company.user && data.company.user.id) {
            this.user = data.company.user;
          }
          if (process.env.VUE_ENV === 'client') {
            if (this.contentShare) {
              this.trigger();
            }
          }
        }
      },
      error(err) {
        this.err = err;
      },
      prefetch: true,
    },
  },
  metaInfo() {
    const head = {
      title: this.$t('contentShares.redirecting-to', { url: '' }).trim(),
      meta: [
        { vmid: 'og:type', property: 'og:type', content: 'website' },
        { vmid: 'twitter:card', name: 'twitter:card', content: 'summary' },
      ],
      links: [],
    };

    if (this.company && ((this.company.siteBranding && this.company.siteBranding.name) || this.company.name)) {
      const companyName = (this.company.siteBranding && this.company.siteBranding.name) || this.company.name;

      head.meta.push({ vmid: 'author', name: 'author', content: companyName });
      head.meta.push({ vmid: 'og:author', name: 'og:author', content: companyName });
      head.meta.push({ vmid: 'og:site_name', property: 'og:site_name', content: companyName });
      // twitter:site => @reallinksio
    }

    if (this.$root && this.$route && this.$root.origin && this.$route.path) {
      const content = `${this.$root.origin}${this.$route.path}`;
      head.links.push({ rel: 'canonical', href: content });
      head.meta.push({ vmid: 'og:url', property: 'og:url', content });
    }

    if (this.contentShare && this.contentShare.id) {
      const { title, excerpt, description } = this.contentShare;
      if (title) {
        head.title = title;
        head.meta.push({ vmid: 'og:title', property: 'og:title', content: title });
        head.meta.push({ vmid: 'twitter:title', property: 'twitter:title', content: title });
      }

      if (description && typeof description === 'string') {
        head.meta.push({ vmid: 'description', property: 'description', content: description });
        head.meta.push({ vmid: 'og:description', property: 'og:description', content: description });
      } else if (excerpt && typeof excerpt === 'string') {
        head.meta.push({ vmid: 'description', property: 'description', content: excerpt });
        head.meta.push({ vmid: 'og:description', property: 'og:description', content: excerpt });
      }

      if (this.ogImage) {
        const content = `${this.$root.origin}${cdnTransform(this.ogImage, 'resize', 1200, 627)}`;

        head.meta.push({ vmid: 'og:image', name: 'image', property: 'og:image', content });
        head.meta.push({ vmid: 'twitter:image', name: 'twitter:image', content });

        head.meta.push({ vmid: 'og:image:width', property: 'og:image:width', content: 1200 });
        head.meta.push({ vmid: 'og:image:height', property: 'og:image:height', content: 627 });

        if (title) {
          head.meta.push({ vmid: 'og:image:alt', property: 'og:image:alt', content: title });
          head.meta.push({ vmid: 'twitter:image:alt', property: 'twitter:image:alt', content: title });
        }
      }

      // Since we're about to redirect to a content URL, set a suitable Referrer-Policy
      head.meta.push({ vmid: 'referrer', name: 'referrer', content: 'unsafe-url' });

      // article:modified_time -> updated at?
      // article:expiration_time -> expiry time?
    }

    return head;
  },
  computed: {
    ...mapRoot([ 'company', 'origin' ]),
    logo() {
      const { siteBranding: { bigLogo, smallLogo } } = this.company || {};
      if (bigLogo && bigLogo.fileLocation) {
        return bigLogo.fileLocation;
      } else if (smallLogo && smallLogo.fileLocation) {
        return smallLogo.fileLocation;
      }
      return null;
    },
    ogImage() {
      const { image, imageURL } = this.contentShare || {};
      const { siteBranding: { bigLogo, socialImage } } = this.company || {};
      if (image && image.fileLocation) {
        return image.fileLocation;
      } else if (imageURL) {
        return imageURL;
      } else if (socialImage && socialImage.fileLocation) {
        return socialImage.fileLocation;
      } else if (bigLogo && bigLogo.fileLocation) {
        return bigLogo.fileLocation;
      } else {
        return null;
      }
    }
  },
  methods: {
    async trigger() {
      const { contentShare, shareCode: contentShareCode } = this;
      this.$assert(contentShare && contentShare.id, new Error('Expected contentShare to exist'));

      const getProperty = (...getters) => {
        if (getters.length) {
          try {
            const getter = getters.shift();
            return getter() || getProperty(...getters) || null;
          } catch (err) {
            return getProperty(...getters) || null;
          }
        } else {
          return null;
        }
      };

      const { id } = contentShare;
      const { url } = this;

      const logVisited = {
        contentShare: id === contentShareCode ? { id } : { shareCode: contentShareCode },
        user: id !== contentShareCode ? { contentShareCode } : undefined,
        url,
        acceptLanguage: getProperty(() => window.navigator.userLanguage, () => window.navigator.language) || undefined,
        referer: getProperty(() => document.referrer) || undefined,
        userAgent: getProperty(() => window.navigator.userAgent) || undefined,
      };

      const captureQueryKeys = [ 'utm_source', 'utm_medium', 'utm_term', 'utm_content', 'utm_campaign' ];
      const queryString = qs.parse(getProperty(() => window.location.search) || '');
      for (const key in queryString) {
        if (queryString.hasOwnProperty(key) && !logVisited.hasOwnProperty(key) && captureQueryKeys.includes(key)) {
          logVisited[key] = queryString[key];
        }
      }

      try {
        await captureLog('CONTENT_SHARE_VISITED', logVisited);
      } catch (err) {
        this.$log.error(err);
      }

      if (url && url.length) {
        setTimeout(() => {
          this.$log.info(`Redirecting to ${url}`);
          window.location = `${url}`;
        }, 500);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/views/content-share/ContentShareTrackRedirect.vue